import {
  Box,
  FlexBox,
  getSelectionListContext,
  Icon,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { MouseEventHandler, useCallback, useContext, useState } from 'react';


import styles from './favoriteItemLayout.module.scss';
import {
  FavoriteToggleFn,
  getFavoriteItemLayoutContext,
} from './favoriteItemLayoutContext';

type FavoriteItemLayoutProps<TItem> = PropsWithChildren & {
  /** The item. */
  item: TItem;
  /** Whether the item is favorited. */
  isFavorite: boolean;
  /** Whether the item is disabled. */
  isDisabled: boolean;
};

/** Represents the layout of an item that can be favorited. */
export const FavoriteItemLayout = <TItem,>({
  item,
  isFavorite,
  isDisabled: disabled,
  children,
}: FavoriteItemLayoutProps<TItem>): JSX.Element => {
  const [favoriteToggle, setFavoriteToggle] = useState<FavoriteToggleFn>();
  const [isFavoriteDisabled, setIsFavoriteDisabled] = useState(false);
  const context = useContext(getSelectionListContext());
  const FavoriteItemLayoutContext = getFavoriteItemLayoutContext();
  const isDisabled = disabled || context.list.isDisabled;

  /** Handles toggling the favorite status. */
  const handleFavoriteToggle = useCallback<MouseEventHandler<SVGSVGElement>>(
    (event) => {
      if (favoriteToggle) {
        favoriteToggle();
        event.stopPropagation();
      }
    },
    [favoriteToggle],
  );

  return (
    <FavoriteItemLayoutContext.Provider
      value={{ item, isFavorite, setFavoriteToggle, setIsFavoriteDisabled }}
    >
      <FlexBox justifyContent="space-between" gap="200">
        <Box
          className={classnames(styles.contentContainer, {
            [styles.isDisabled]: isDisabled,
          })}
        >
          {children}
        </Box>

        <FlexBox
          alignItems="center"
          className={classnames(styles.iconContainer, {
            [styles.isDisabled]: isFavoriteDisabled,
          })}
        >
          <Icon
            className={classnames(styles.icon, {
              [styles.isFavorite]: isFavorite,
            })}
            name={isFavorite ? 'star-full' : 'star'}
            onClick={handleFavoriteToggle}
          />
        </FlexBox>
      </FlexBox>
    </FavoriteItemLayoutContext.Provider>
  );
};
